// File: src/features/jobs/WsLogViewer.tsx
import { useEffect, useRef, useState, useMemo } from 'react';
import { useKeycloak } from '@react-keycloak/web';
import { useNavigate } from 'react-router-dom';
import { useLiveJobStore } from '../../store/liveJobStore';

interface Toast {
  id: string;
  message: string;
  timestamp: string;
  type: 'info' | 'success' | 'warning' | 'error';
  traceId?: string;
  jobId?: string;
  documentId?: string;
}

// Document processing job types for identification
const DOCUMENT_JOBS = [
  'DocumentUploadIngestJob',
  'TextExtractionJob', 
  'MetadataExtractionJob',
  'DocumentVectorizationJob',
  'DocumentIngestJob'
];

export default function WsLogViewer() {
  const { keycloak } = useKeycloak();
  const navigate = useNavigate();
  const [toastQueue, setToastQueue] = useState<Toast[]>([]);
  const [showSchemaErrors, setShowSchemaErrors] = useState(false);
  const logContainerRef = useRef<HTMLDivElement>(null);

  // Use the persistent Zustand store
  const {
    wsLogs,
    wsLogsPaused,
    connected,
    initializeSocket,
    toggleWsLogsPause,
    clearWsLogs
  } = useLiveJobStore();

  // Filter state management
  const [selectedChannels, setSelectedChannels] = useState<string[]>([]);
  const [selectedRealms, setSelectedRealms] = useState<string[]>([]);
  const [selectedJobs, setSelectedJobs] = useState<string[]>([]);

  // Get filtered logs with proper reactivity
  const filteredLogs = useMemo(() => {
    console.log('[WsLogViewer] 🔍 Filtering logs...', {
      totalLogs: wsLogs.length,
      selectedChannels,
      selectedRealms,
      selectedJobs
    });

    return wsLogs.filter((log) => {
      // Channel filter - if any channels selected, only show those
      if (selectedChannels.length > 0 && !selectedChannels.includes(log.channel)) {
        return false;
      }
      
      // Realm filter - if any realms selected, only show those
      if (selectedRealms.length > 0 && log.realmId && !selectedRealms.includes(log.realmId)) {
        return false;
      }
      
      // Job filter - if any jobs selected, only show those
      if (selectedJobs.length > 0 && log.jobName && !selectedJobs.includes(log.jobName)) {
        return false;
      }
      
      return true;
    });
  }, [wsLogs, selectedChannels, selectedRealms, selectedJobs]);

  const showToast = (message: string, type: Toast['type'] = 'info', options?: Partial<Toast>) => {
    const id = `${Date.now()}-${Math.random().toString(36).slice(2)}`;
    const timestamp = new Date().toISOString();
    setToastQueue((prev) => [...prev, { 
      id, 
      message, 
      timestamp, 
      type,
      ...options 
    }]);

    // Auto-dismiss after varying durations based on type
    const duration = type === 'error' ? 15000 : type === 'warning' ? 12000 : 10000;
    setTimeout(() => {
      setToastQueue((prev) => prev.filter((t) => t.id !== id));
    }, duration);
  };

  const dismissToast = (id: string) => {
    setToastQueue((prev) => prev.filter((t) => t.id !== id));
  };

  const isDocumentJob = (jobName?: string) => {
    return jobName && DOCUMENT_JOBS.some(docJob => jobName.includes(docJob));
  };

  // Initialize WebSocket connection through the store
  useEffect(() => {
    if (!keycloak?.token) {
      console.warn('[WsLogViewer] ⚠️ No Keycloak token available. Skipping socket connection.');
      return;
    }

    // Initialize socket through the store if not already connected
    if (!connected) {
      console.log('[WsLogViewer] 🚀 Initializing WebSocket connection through store...');
      console.log(`[WsLogViewer] 🔑 Token: ${keycloak.token.substring(0, 20)}...`);
      console.log(`[WsLogViewer] 🌐 Admin mode: true (all realms)`);
      initializeSocket(keycloak.token, null, true); // Admin mode for all realms
    } else {
      console.log('[WsLogViewer] ✅ Socket already connected');
    }
  }, [keycloak?.token, connected, initializeSocket]);

  // Debug log state changes
  useEffect(() => {
    console.log(`[WsLogViewer] 📊 State update - Connected: ${connected}, Logs: ${wsLogs.length}, Paused: ${wsLogsPaused}`);
  }, [connected, wsLogs.length, wsLogsPaused]);

  // Monitor logs for toasts (since the store handles all event capture now)
  useEffect(() => {
    if (filteredLogs.length === 0) return;
    
    const latestLog = filteredLogs[0];
    if (!latestLog) return;

    // Show toasts for important events
    if (latestLog.level === 'error') {
      showToast(`🔧 Error: ${latestLog.message || 'Unknown error'}`, 'error', {
        jobId: latestLog.jobId
      });
    } else if (latestLog.channel.includes('job:completed') && isDocumentJob(latestLog.jobName)) {
      showToast(`📄 Document Job Completed: ${latestLog.jobName}`, 'success', {
        jobId: latestLog.jobId
      });
    } else if (latestLog.channel.includes('job:failed')) {
      showToast(`❌ Job Failed: ${latestLog.jobName}`, 'error', {
        jobId: latestLog.jobId
      });
    }
  }, [filteredLogs]);

  // Auto-scroll to top when NEW logs arrive (not when filters change)
  const [lastLogCount, setLastLogCount] = useState(0);
  
  useEffect(() => {
    if (wsLogs.length > lastLogCount && !wsLogsPaused) {
      // New logs arrived - auto scroll to top (newest first)
      if (logContainerRef.current) {
        logContainerRef.current.scrollTo({
          top: 0,
          behavior: 'smooth'
        });
      }
    }
    setLastLogCount(wsLogs.length);
  }, [wsLogs.length, lastLogCount, wsLogsPaused]);

  const uniqueChannels = useMemo(() => {
    const channels = new Set(wsLogs.map(log => log.channel));
    return Array.from(channels).sort();
  }, [wsLogs]);

  const uniqueRealms = useMemo(() => {
    const realms = new Set(wsLogs.map(log => log.realmId).filter((realm): realm is string => Boolean(realm)));
    return Array.from(realms).sort();
  }, [wsLogs]);

  const uniqueJobs = useMemo(() => {
    const jobs = new Set(wsLogs.map(log => log.jobName).filter((job): job is string => Boolean(job)));
    return Array.from(jobs).sort();
  }, [wsLogs]);

  const toggleChannelFilter = (channel: string) => {
    setSelectedChannels(prev => 
      prev.includes(channel) 
        ? prev.filter(c => c !== channel)
        : [...prev, channel]
    );
  };

  const toggleRealmFilter = (realm: string) => {
    setSelectedRealms(prev => 
      prev.includes(realm) 
        ? prev.filter(r => r !== realm)
        : [...prev, realm]
    );
  };

  const toggleJobFilter = (job: string) => {
    setSelectedJobs(prev => 
      prev.includes(job) 
        ? prev.filter(j => j !== job)
        : [...prev, job]
    );
  };

  const clearAllFilters = () => {
    setSelectedChannels([]);
    setSelectedRealms([]);
    setSelectedJobs([]);
  };

  const getChannelIcon = (channel: string) => {
    if (channel.includes('job:created')) return '🆕';
    if (channel.includes('job:started')) return '🚀';
    if (channel.includes('job:completed')) return '✅';
    if (channel.includes('job:failed')) return '❌';
    if (channel.includes('job:progress')) return '⏳';
    if (channel.includes('agent:')) return '🤖';
    if (channel.includes('connection')) return '🔌';
    return '📡';
  };

  // Handle job ID click navigation
  const handleJobIdClick = (jobId: string, e: React.MouseEvent) => {
    e.preventDefault();
    e.stopPropagation();
    
    console.log('[WsLogViewer] 🎯 Navigating to job:', jobId);
    
    // Navigate to the job trace page
    navigate(`/system-monitor/job-trace/${jobId}`);
    
    // Show toast notification
    showToast(`🔍 Opening job details for ${jobId.split(':')[1]?.substring(0, 8)}...`, 'info');
  };

  return (
    <div className="flex flex-col h-full bg-gray-50 dark:bg-gray-900 transition-colors duration-300">
      {/* Header */}
      <div className="flex-shrink-0 bg-white dark:bg-gray-800 border-b border-gray-200 dark:border-gray-700 px-6 py-4 transition-colors duration-300">
        <div className="flex items-center justify-between">
          <div className="flex items-center space-x-4">
            <h1 className="text-2xl font-bold text-gray-900 dark:text-white transition-colors duration-300">
              WebSocket Logs
            </h1>
            <div className="flex items-center space-x-2">
              <div className={`w-3 h-3 rounded-full ${connected ? 'bg-green-400' : 'bg-red-400'}`}></div>
              <span className="text-sm text-gray-600 dark:text-gray-400">
                {connected ? 'Connected' : 'Disconnected'}
              </span>
            </div>
          </div>
          
          <div className="flex items-center space-x-3">
            <button
              onClick={() => {
                console.log('[WsLogViewer] 🧪 Testing WebSocket connection...');
                console.log('[WsLogViewer] 📊 Current logs:', wsLogs.length);
                console.log('[WsLogViewer] 🔌 Connected:', connected);
                console.log('[WsLogViewer] 🔍 Filter State:', {
                  selectedChannels,
                  selectedRealms,
                  selectedJobs,
                  filteredCount: filteredLogs.length
                });
                if (wsLogs.length > 0) {
                  console.log('[WsLogViewer] 📋 Recent logs:', wsLogs.slice(0, 5));
                }
                if (filteredLogs.length > 0) {
                  console.log('[WsLogViewer] 🎯 Filtered logs:', filteredLogs.slice(0, 3));
                }
              }}
              className="px-3 py-2 bg-purple-600 hover:bg-purple-700 text-white rounded-lg font-medium transition-colors text-sm"
            >
              🧪 Debug
            </button>
            
            <button
              onClick={toggleWsLogsPause}
              className={`px-4 py-2 rounded-lg font-medium transition-colors ${
                wsLogsPaused
                  ? 'bg-green-600 hover:bg-green-700 text-white'
                  : 'bg-yellow-600 hover:bg-yellow-700 text-white'
              }`}
            >
              {wsLogsPaused ? 'Resume' : 'Pause'}
            </button>
            
            <button
              onClick={clearWsLogs}
              className="px-4 py-2 bg-red-600 hover:bg-red-700 text-white rounded-lg font-medium transition-colors"
            >
              Clear
            </button>
          </div>
        </div>
      </div>

             {/* Filters */}
       <div className="flex-shrink-0 bg-white dark:bg-gray-800 border-b border-gray-200 dark:border-gray-700 px-6 py-4 transition-colors duration-300">
         {/* Filter Summary */}
         <div className="flex items-center justify-between mb-4">
           <div className="text-sm text-gray-600 dark:text-gray-400">
             {filteredLogs.length} log{filteredLogs.length !== 1 ? 's' : ''} 
             {(selectedChannels.length > 0 || selectedRealms.length > 0 || selectedJobs.length > 0) && (
               <span className="ml-2 px-2 py-1 bg-lex-accent-soft text-lex-accent rounded text-xs">
                 Filtered
               </span>
             )}
           </div>
           <button
             onClick={clearAllFilters}
             className="px-3 py-1 text-xs text-gray-600 dark:text-gray-400 hover:text-gray-800 dark:hover:text-gray-200 transition-colors"
           >
             Clear Filters
           </button>
         </div>

         {/* Channel Filters */}
         {uniqueChannels.length > 0 && (
           <div className="mb-3">
             <label className="text-xs font-medium text-gray-700 dark:text-gray-300 mb-2 block">Channels:</label>
             <div className="flex flex-wrap gap-2">
               {uniqueChannels.map(channel => (
                 <button
                   key={channel}
                   onClick={() => toggleChannelFilter(channel)}
                   className={`px-3 py-1 text-xs font-medium rounded flex items-center gap-1 transition-colors ${
                     selectedChannels.includes(channel)
                       ? 'bg-lex-accent text-lex-accent-contrast'
                       : 'bg-gray-100 text-gray-700 hover:bg-gray-200 dark:bg-gray-700 dark:text-gray-300 dark:hover:bg-gray-600'
                   }`}
                 >
                   <span>{getChannelIcon(channel)}</span>
                   <span>{channel}</span>
                   {selectedChannels.includes(channel) && (
                     <span className="ml-1 bg-white bg-opacity-20 px-1 rounded text-xs">
                       {wsLogs.filter(log => log.channel === channel).length}
                     </span>
                   )}
                 </button>
               ))}
             </div>
           </div>
         )}

         {/* Realm Filters */}
         {uniqueRealms.length > 0 && (
           <div className="mb-3">
             <label className="text-xs font-medium text-gray-700 dark:text-gray-300 mb-2 block">Realms:</label>
             <div className="flex flex-wrap gap-2">
               {uniqueRealms.map(realm => (
                 <button
                   key={realm}
                   onClick={() => toggleRealmFilter(realm)}
                   className={`px-3 py-1 text-xs font-medium rounded flex items-center gap-1 transition-colors ${
                     selectedRealms.includes(realm)
                       ? 'bg-green-600 text-white'
                       : 'bg-gray-100 text-gray-700 hover:bg-gray-200 dark:bg-gray-700 dark:text-gray-300 dark:hover:bg-gray-600'
                   }`}
                 >
                   <span>🏢</span>
                   <span>{realm}</span>
                 </button>
               ))}
             </div>
           </div>
         )}

         {/* Job Filters */}
         {uniqueJobs.length > 0 && (
           <div>
             <label className="text-xs font-medium text-gray-700 dark:text-gray-300 mb-2 block">Job Types:</label>
             <div className="flex flex-wrap gap-2">
               {uniqueJobs.map(job => (
                 <button
                   key={job}
                   onClick={() => toggleJobFilter(job)}
                   className={`px-3 py-1 text-xs font-medium rounded flex items-center gap-1 transition-colors ${
                     selectedJobs.includes(job)
                       ? 'bg-purple-600 text-white'
                       : 'bg-gray-100 text-gray-700 hover:bg-gray-200 dark:bg-gray-700 dark:text-gray-300 dark:hover:bg-gray-600'
                   }`}
                 >
                   <span>{isDocumentJob(job) ? '📄' : '⚙️'}</span>
                   <span>{job}</span>
                 </button>
               ))}
             </div>
           </div>
         )}
       </div>

      {/* Logs Display */}
      <div 
        ref={logContainerRef}
        className="flex-1 overflow-y-auto p-4 bg-gray-100 dark:bg-gray-850 text-black dark:text-gray-300 border-t border-gray-300 dark:border-gray-700 transition-colors duration-300"
      >
        {filteredLogs.map((log) => {
          const level = log.level || 'info';
          const isSystemLog = log.channel === 'system:log';
          const isDocumentEvent = log.channel === 'chat:documentProcessed' || isDocumentJob(log.jobName);
          
          const levelColor =
            level === 'error'
              ? 'text-red-400'
              : level === 'warn'
              ? 'text-yellow-300'
              : level === 'debug'
              ? 'text-purple-300'
              : isSystemLog
              ? 'text-lex-accent'
              : isDocumentEvent
              ? 'text-green-300'
              : 'text-green-300';

          const channelStyle = isSystemLog 
            ? 'font-bold text-lex-accent'
            : isDocumentEvent 
            ? 'font-bold text-green-400'
            : 'font-semibold';

          return (
            <div key={log.id} className={`mb-2 p-2 border-l-4 ${
              level === 'error' ? 'border-red-400' :
              level === 'warn' ? 'border-yellow-400' :
              isDocumentEvent ? 'border-green-400' :
              'border-lex-accent'
            } bg-gray-50 dark:bg-gray-800 rounded transition-colors duration-300`}>
              <div className="flex items-start space-x-2 text-sm">
                <span className="text-gray-500 dark:text-gray-400 text-xs">
                  {new Date(log.timestamp).toLocaleTimeString()}
                </span>
                <span className={channelStyle}>
                  {log.icon} {log.channel}
                </span>
                {log.realmId && (
                  <span className="text-xs text-gray-500 dark:text-gray-400 bg-gray-200 dark:bg-gray-700 px-1 rounded">
                    {log.realmId}
                  </span>
                )}
                {log.jobId && (
                  <button
                    onClick={(e) => handleJobIdClick(log.jobId!, e)}
                    className="inline-flex items-center gap-1 text-xs bg-lex-accent hover:bg-lex-accent-hover text-lex-accent-contrast px-2 py-1 rounded-md transition-all duration-200 hover:shadow-md cursor-pointer group"
                    title={`Click to view job details for ${log.jobId}`}
                  >
                    <span>🔍</span>
                    <span className="font-mono">
                      {log.jobId.split(':')[1]?.substring(0, 8)}...
                    </span>
                    <span className="opacity-0 group-hover:opacity-100 transition-opacity">
                      →
                    </span>
                  </button>
                )}
              </div>
              
              <div className={`mt-1 ${levelColor}`}>
                {log.message || 'No message'}
              </div>
              
              {log.payload && (
                <details className="mt-2">
                  <summary className="text-xs text-gray-500 dark:text-gray-400 cursor-pointer hover:text-gray-700 dark:hover:text-gray-300">
                    View Payload
                  </summary>
                  <pre className="mt-1 text-xs text-gray-600 dark:text-gray-400 bg-gray-200 dark:bg-gray-700 p-2 rounded overflow-x-auto transition-colors duration-300">
                    {JSON.stringify(log.payload, null, 2)}
                  </pre>
                </details>
              )}
            </div>
          );
        })}
        
        {filteredLogs.length === 0 && (
          <div className="text-center text-gray-500 dark:text-gray-400 py-8">
            No WebSocket logs to display. {wsLogsPaused ? 'Logging is paused.' : 'Waiting for events...'}
          </div>
        )}
        
        {/* Scroll to Top Button */}
        {filteredLogs.length > 5 && (
          <button
            onClick={() => {
              if (logContainerRef.current) {
                logContainerRef.current.scrollTo({
                  top: 0,
                  behavior: 'smooth'
                });
              }
            }}
            className="fixed bottom-6 right-6 bg-lex-accent hover:bg-lex-accent-hover text-lex-accent-contrast p-3 rounded-full shadow-lg transition-colors z-40"
            title="Scroll to newest logs"
          >
            ↑
          </button>
        )}
      </div>

      {/* Toasts */}
      <div className="fixed top-4 right-4 space-y-2 z-50">
        {toastQueue.map((toast) => (
          <div
            key={toast.id}
            className={`px-4 py-3 rounded-lg shadow-lg border-l-4 transition-all duration-300 ${
              toast.type === 'error'
                ? 'bg-red-50 border-red-400 text-red-800'
                : toast.type === 'warning'
                ? 'bg-yellow-50 border-yellow-400 text-yellow-800'
                : toast.type === 'success'
                ? 'bg-green-50 border-green-400 text-green-800'
                : 'bg-lex-accent-soft border-lex-accent text-lex-accent'
            }`}
          >
            <div className="flex items-center justify-between">
              <span className="text-sm font-medium">{toast.message}</span>
              <button
                onClick={() => dismissToast(toast.id)}
                className="ml-4 text-gray-400 hover:text-gray-600"
              >
                ×
              </button>
            </div>
          </div>
        ))}
      </div>

      {/* Schema Errors Viewer - Placeholder for future implementation */}
      {showSchemaErrors && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
          <div className="bg-white dark:bg-gray-800 p-6 rounded-lg max-w-lg w-full mx-4">
            <h3 className="text-lg font-semibold mb-4">Schema Validation Errors</h3>
            <p className="text-gray-600 dark:text-gray-400 mb-4">Schema error tracking coming soon...</p>
            <button
              onClick={() => setShowSchemaErrors(false)}
              className="px-4 py-2 bg-lex-accent text-lex-accent-contrast rounded hover:bg-lex-accent-hover"
            >
              Close
            </button>
          </div>
        </div>
      )}
    </div>
  );
}
